import React, { useEffect, useState } from "react";
import { getAllClaimedFestivals, getUsersClaimedFestival, updateClaimStatus } from "../../services/festival";

// MUI Imports
import {
    Box, Typography, TextField, Accordion, AccordionSummary, AccordionDetails,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Select, MenuItem, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, Button, Pagination, Avatar, Chip, FormControl, CircularProgress, Alert
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getImageUrlAdminToken } from '../../utills/ImagesUtilsAdmin';
import { useNavigate } from "react-router-dom";
//import the correct path for the snackbar context
import { useSnackbar } from "../../../context/SnackbarProvider";


const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const getStatusColor = (status) => {
    switch (status) {
        case "pending": return "warning";
        case "under_review": return "info";
        case "approved": return "success";
        case "declined": return "error";
        default: return "default";
    }
};

const formatStatusText = (status) => {
    switch (status) {
        case "pending": return "Pending";
        case "under_review": return "Under Process";
        case "approved": return "Approved";
        case "declined": return "Declined";
        default: return status.replace("_", " ");
    }
};

const Claims = () => {
    const { showSnackbar } = useSnackbar();
    const navigate = useNavigate();
    // --- Festival State ---
    const [festivalData, setFestivalData] = useState([]);
    const [festivalSearch, setFestivalSearch] = useState("");
    const [festivalPage, setFestivalPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [isFestivalLoading, setIsFestivalLoading] = useState(true);
    const [festivalError, setFestivalError] = useState(null);

    // --- Claims State (Inner Table) ---
    const [expandedFestivalId, setExpandedFestivalId] = useState(false);
    const [claimsData, setClaimsData] = useState(null);
    const [claimsSearch, setClaimsSearch] = useState("");
    const [claimsPage, setClaimsPage] = useState(1);
    const [claimsLastPage, setClaimsLastPage] = useState(1);
    const [isClaimsLoading, setIsClaimsLoading] = useState(false);
    const [claimsError, setClaimsError] = useState(null);


    // --- Dialog & Update State ---
    const [dialogOpen, setDialogOpen] = useState(false);
    const [pendingUpdate, setPendingUpdate] = useState({ festivalId: null, claimId: null, newStatus: "" });
    const [isUpdateLoading, setIsUpdateLoading] = useState(false);


    // 1. Fetch Festivals
    useEffect(() => {
        const fetchFestivals = async () => {
            setIsFestivalLoading(true);
            setFestivalError(null);
            try {
                const data = await getAllClaimedFestivals(festivalSearch, festivalPage);
                console.log("Fetched festivals:", data);
                setFestivalData(data.film_festivals);
                setLastPage(data.last_page || 1);
            } catch (error) {
                console.error("Error fetching claimed festivals:", error);
                setFestivalError("Failed to load festivals. Please try again.");
            } finally {
                setIsFestivalLoading(false);
            }
        };
        const delayDebounce = setTimeout(() => fetchFestivals(), 300);
        return () => clearTimeout(delayDebounce);
    }, [festivalSearch, festivalPage]);

    // 2. Fetch Inner Claims
    useEffect(() => {
        const fetchClaims = async () => {
            if (!expandedFestivalId) return;

            setIsClaimsLoading(true);
            setClaimsError(null);
            try {
                const data = await getUsersClaimedFestival(expandedFestivalId, claimsSearch, claimsPage);
                console.log("Fetched claims for festival", expandedFestivalId, ":", data);
                setClaimsData(data.film_festival_claims);
                setClaimsLastPage(data.last_page || 1);
            } catch (error) {
                console.error("Error fetching users claimed festival:", error);
                setClaimsError("Failed to load claims for this festival.");
            } finally {
                setIsClaimsLoading(false);
            }
        };
        const delayDebounce = setTimeout(() => fetchClaims(), 300);
        return () => clearTimeout(delayDebounce);
    }, [expandedFestivalId, claimsSearch, claimsPage]);

    // --- Handlers ---
    const handleFestivalSearch = (e) => {
        setFestivalSearch(e.target.value);
        setFestivalPage(1);
    };

    const handleClaimsSearch = (e) => {
        setClaimsSearch(e.target.value);
        setClaimsPage(1);
    };

    const handleAccordionToggle = (panelId) => (event, isExpanded) => {
        setExpandedFestivalId(isExpanded ? panelId : false);
        if (isExpanded) {
            setClaimsSearch("");
            setClaimsPage(1);
            setClaimsData(null);
        }
    };

    // --- Dialog & Update Flow ---
    const handleStatusDropdownChange = (festivalId, claimId, event) => {
        setPendingUpdate({ festivalId, claimId, newStatus: event.target.value });
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        if (isUpdateLoading) return; // Prevent closing while updating
        setDialogOpen(false);
        setPendingUpdate({ festivalId: null, claimId: null, newStatus: "" });
    };

    const handleConfirmStatusChange = async () => {
        const { festivalId, claimId, newStatus } = pendingUpdate;
        setIsUpdateLoading(true);
        try {
            const res = await updateClaimStatus(festivalId, claimId, newStatus);
            console.log("Status update response:", res);
            showSnackbar(res?.message || "Status updated successfully", "success");
            // Update UI instantly
            setClaimsData(prev =>
                prev.map(claim =>
                    claim.film_festival_claims_id === claimId
                        ? { ...claim, claim_request_status: newStatus }
                        : claim
                )
            );
            handleCloseDialog();
        } catch (error) {
            console.error("Failed to update status", error);
            showSnackbar(error?.response?.data?.message || "Failed to update status", "error");
        } finally {
            setIsUpdateLoading(false);
        }
    };

    const handleOpenUserDetails = (userId) => {
        if (userId) {
            navigate(`/users/${userId}`);
        } else {
            showSnackbar("User details not available", "error");
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>

            {/* Header & Outer Search */}
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
                flexWrap: 'wrap',
                gap: 2
            }}>
                <Box>
                    <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>
                        Festival Claims
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage and review festival claim requests
                    </Typography>
                </Box>
                <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search festivals..."
                    value={festivalSearch}
                    onChange={handleFestivalSearch}
                    sx={{
                        width: { xs: '100%', sm: 300 },
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                        }
                    }}
                />
            </Box>

            {/* Festival Loading / Error / Data States */}
            {festivalError && (
                showSnackbar(festivalError, "error")
            )}

            {isFestivalLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : (
                festivalData.length === 0 ? (
                    <Box sx={{
                        textAlign: 'center',
                        py: 8,
                        px: 3,
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 2,
                        bgcolor: 'action.hover'
                    }}>
                        <Typography variant="h6" color="text.secondary" fontWeight={500}>
                            No festivals found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {festivalSearch ? 'Try adjusting your search criteria' : 'No claimed festivals available'}
                        </Typography>
                    </Box>
                ) : (festivalData.map((festival) => (
                    <Accordion
                        key={festival.film_festival_id}
                        expanded={expandedFestivalId === festival.film_festival_id}
                        onChange={handleAccordionToggle(festival.film_festival_id)}
                        elevation={0}
                        sx={{
                            mb: 1,
                            borderRadius: 2,
                            '&:before': { display: 'none' },
                            '&.Mui-expanded': { mb: 1, mt: 0 } // Maintain margin when expanded
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel-${festival.film_festival_id}-content`}
                            id={`panel-${festival.film_festival_id}-header`}>

                            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar
                                        src={getImageUrlAdminToken(festival.film_festival_logo)}
                                        alt={festival.film_festival_name}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 1 }}>
                                        <Typography variant="subtitle1" fontWeight={600}
                                            sx={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 1,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                lineHeight: 1,
                                            }}
                                        >
                                            {festival.film_festival_name}



                                        </Typography>

                                        <Chip
                                            label={`Claims : ${festival.total_claims}`}
                                            size="small"
                                            sx={{
                                                mr: 1,
                                                fontWeight: 500,
                                                bgcolor: 'primary.light',
                                                color: '#fcfcfc',
                                            }}
                                            clickable={false}
                                            onClick={(e) => { }}
                                        />
                                        {/* <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 1,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                lineHeight: 1,
                                            }}
                                        >
                                            {festival.film_festival_description}
                                        </Typography> */}
                                    </Box>
                                </Box>

                                {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="body2" fontWeight={700} color="primary.dark">
                                        Claims : {festival.total_claims}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700} color="success.dark">
                                        Approved : {festival.approved_claims}
                                    </Typography>

                                    <Typography variant="body2" fontWeight={700} color="error.dark">
                                        Declined : {festival.declined_claims}
                                    </Typography>

                                    <Typography variant="body2" fontWeight={700} color="warning.dark">
                                        Pending : {festival.pending_claims}
                                    </Typography>
                                </Box> */}
                            </Box>

                        </AccordionSummary>


                        <AccordionDetails>
                            <Box>
                                {/* <TextField
                                    size="small"
                                    variant="outlined"
                                    placeholder="Search by name, email, or phone..."
                                    value={claimsSearch}
                                    onChange={handleClaimsSearch}
                                    sx={{
                                        width: '100%',
                                        mb: 2,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2
                                        }
                                    }}
                                /> */}
                                {claimsError && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {claimsError}
                                    </Alert>
                                )}
                                {isClaimsLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    claimsData && claimsData.length > 0 ? (
                                        <TableContainer sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 2,
                                            overflowX: 'auto',
                                            '&::-webkit-scrollbar': {
                                                height: 8
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                bgcolor: 'action.hover',
                                                borderRadius: 1
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                bgcolor: 'divider',
                                                borderRadius: 1,
                                                '&:hover': {
                                                    bgcolor: 'text.secondary'
                                                }
                                            }
                                        }}>
                                            <Table sx={{ minWidth: 650 }} aria-label="claims table">
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                                                        <TableCell sx={{
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            color: 'text.secondary',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: 0.5,
                                                            py: 1.5
                                                        }}>Requester</TableCell>
                                                        <TableCell sx={{
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            color: 'text.secondary',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: 0.5,
                                                            py: 1.5
                                                        }}>Contact</TableCell>
                                                        <TableCell sx={{
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            color: 'text.secondary',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: 0.5,
                                                            py: 1.5
                                                        }}>Requested On</TableCell>
                                                        <TableCell sx={{
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            color: 'text.secondary',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: 0.5,
                                                            py: 1.5
                                                        }}>Status</TableCell>
                                                        <TableCell sx={{
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            color: 'text.secondary',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: 0.5,
                                                            py: 1.5
                                                        }}>Action</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {claimsData.map((claim, index) => (
                                                        <TableRow
                                                            key={claim.film_festival_claims_id}
                                                            sx={{
                                                                '&:hover': { bgcolor: 'action.hover' },
                                                                borderBottom: index === claimsData.length - 1 ? 'none' : '1px solid',
                                                                borderColor: 'divider'
                                                            }}
                                                        >
                                                            <TableCell sx={{ py: 2 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
                                                                    onClick={() => handleOpenUserDetails(claim.user_id)}
                                                                >
                                                                    <Avatar />
                                                                    <Box>
                                                                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25, textTransform: 'capitalize' }}>
                                                                            {claim.name || "N/A"}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                                            {claim.username || "N/A"}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell sx={{ py: 2 }}>
                                                                <Box>
                                                                    <Typography variant="body2" sx={{ mb: 0.25 }}>
                                                                        {claim.email || "N/A"}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                                        {claim.contact_number || "N/A"}
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell sx={{ py: 2 }}>
                                                                <Typography variant="body2">
                                                                    {formatDate(claim.created_at)}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ py: 2 }}>
                                                                <Chip
                                                                    label={formatStatusText(claim.claim_request_status)}
                                                                    color={getStatusColor(claim.claim_request_status)}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                                                                    clickable={false}
                                                                    onClick={(e) => { }}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ py: 2 }}>
                                                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                                                    <Select
                                                                        value={claim.claim_request_status}
                                                                        onChange={(e) => handleStatusDropdownChange(expandedFestivalId, claim.film_festival_claims_id, e)}
                                                                        displayEmpty
                                                                        sx={{
                                                                            fontSize: '0.875rem',
                                                                            borderRadius: 1.5,
                                                                            '& .MuiSelect-select': {
                                                                                py: 1
                                                                            }
                                                                        }}
                                                                    >
                                                                        <MenuItem value="pending">Pending</MenuItem>
                                                                        <MenuItem value="under_review">Under Process</MenuItem>
                                                                        <MenuItem value="approved">Approved</MenuItem>
                                                                        <MenuItem value="declined">Declined</MenuItem>
                                                                    </Select>
                                                                </FormControl>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : (
                                        <Box sx={{
                                            textAlign: 'center',
                                            py: 8,
                                            px: 3,
                                            border: '1px dashed',
                                            borderColor: 'divider',
                                            borderRadius: 2,
                                            bgcolor: 'action.hover'
                                        }}>
                                            <Typography variant="body1" color="text.secondary" fontWeight={500}>
                                                No claims found for this festival
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                Try adjusting your search criteria
                                            </Typography>
                                        </Box>
                                    )
                                )}
                                {/* Claims Pagination */}
                                {claimsLastPage > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                        <Pagination
                                            count={claimsLastPage}
                                            page={claimsPage}
                                            onChange={(e, value) => setClaimsPage(value)}
                                            color="primary"
                                            variant="outlined"
                                            shape="rounded"
                                        />
                                    </Box>
                                )}
                            </Box>
                        </AccordionDetails>

                    </Accordion>

                ))
                )
            )}
            {/* Festival Pagination */}
            {lastPage > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination
                        count={lastPage}
                        page={festivalPage}
                        onChange={(e, value) => setFestivalPage(value)}
                        color="primary"
                        variant="outlined"
                        shape="rounded"
                    />
                </Box>
            )}




            {/* Confirmation Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        minWidth: { xs: '90%', sm: 400 }
                    }
                }}
            >
                <DialogTitle>
                    Confirm Status Change
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to change the status of this claim to <strong>{formatStatusText(pendingUpdate.newStatus)}</strong>? This action will notify the user.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        onClick={handleCloseDialog}
                        color="inherit"
                        disabled={isUpdateLoading}
                        sx={{
                            textTransform: 'none',
                            borderRadius: 2,
                            px: 2.5
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmStatusChange}
                        color="primary"
                        variant="contained"
                        autoFocus
                        disabled={isUpdateLoading}
                        startIcon={isUpdateLoading ? <CircularProgress size={20} color="inherit" /> : null}
                        sx={{
                            textTransform: 'none',
                            borderRadius: 2,
                            px: 3,
                            fontWeight: 600
                        }}
                    >
                        {isUpdateLoading ? "Updating..." : "Confirm"}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default Claims;